// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import {translationStore} from '../../stores/translation-store';
import {Button, Link} from '@deere/isg.component-library';
import {StoreBadge} from './StoreBadge';
import {linkStore} from '../../stores/link-store';
import AppEm from '/assets/app-em.svg';
import AppOcm from '/assets/app-ocm.svg';
import QrEm from '/assets/qr-em.svg';
import QrOcm from '/assets/qr-ocm.svg';

function getAppBadges(isOpsCenterMobile: boolean, isMobile: boolean, translations: Record<string, string>) {
    const equipmentManagerQRCode = QrEm;
    const operationCenterQRCode = QrOcm;
    const equipmentMobileImage = AppEm;
    const operationCenterImage = AppOcm;

    let appTitle = 'Equipment Mobile';
    let appSubtitle = translations.OPS_CENTER_LANDING_EQUIPMENT_MOBILE_NEW_DESCRIPTION;
    let iosUrl = 'https://apps.apple.com/us/app/equipment-mobile/id1594839117';
    let androidUrl = 'https://play.google.com/store/apps/details?id=com.deere.runapp';
    let appImage = equipmentMobileImage;
    let appQR = equipmentManagerQRCode;

    if (isOpsCenterMobile) {
        appTitle = 'Operations Center Mobile';
        appSubtitle = translations.OPS_CENTER_LANDING_OPS_CENTER_MOBILE_DESCRIPTION;
        iosUrl = 'https://apps.apple.com/us/app/operations-center-mobile/id1104383066';
        androidUrl = 'https://play.google.com/store/apps/details?id=com.deere.myoperations';
        appImage = operationCenterImage;
        appQR = operationCenterQRCode;
    }

    return (
        <div className={`${isMobile ? 'w-full' : 'w-2/5'} flex flex-col items-start gap-3 mt-4`}>
            <div className='flex gap-4'>
                <img alt={`App icon for ${appTitle} app`} className='h-16 w-16' src={appImage} />
                {!isMobile && <img alt={`QR Code for ${appTitle} app`} className='h-16 w-16' src={appQR} />}
            </div>
            <h3
                className={`text-white flex-none font-[Open_Sans,serif] ${isMobile ? 'text-base font-bold leading-[22px]' : 'text-base font-semibold leading-6'} max-w-[300px] overflow-hidden text-ellipsis m-0`}
            >
                {appTitle}
            </h3>
            <p className='text-[#b3b3b3] flex-none font-[Open_Sans,serif] text-sm font-normal leading-5 max-w-[300px] overflow-hidden text-ellipsis m-0'>
                {appSubtitle}
            </p>
            {isMobile && (
                <div className='flex gap-4'>
                    <StoreBadge platform='ios' url={iosUrl} />
                    <StoreBadge platform='android' url={androidUrl} />
                </div>
            )}
        </div>
    );
}

interface LandingPageContentProps {
    isMobile: boolean;
}

export const LandingPageContent = ({isMobile}: LandingPageContentProps): React.ReactElement => {
    return (
        <div className={`flex-[50%] content-center font-normal grid gap-8 ${!isMobile ? 'max-w-[800px]' : ''}`}>
            <h1
                className='landingPageText'
                dangerouslySetInnerHTML={{__html: translationStore.OPS_CENTER_LANDING_MAIN_TEXT}}
            />
            <div className='flex gap-4 mt-4'>
                <Link href='/api/login' target='_self' underline='none'>
                    <Button variant='primary' size={'large'}>
                        {translationStore.loginPage_loginButton}
                    </Button>
                </Link>
                <Link href={linkStore.createAccountUrl} target='_self' underline='none'>
                    <Button sx={{backgroundColor: '#FFF'}} size='large' variant='secondary'>
                        {translationStore.OPS_CENTER_LANDING_CREATE_ACCOUNT}
                    </Button>
                </Link>
            </div>
            <div className='flex flex-col items-start p-0 gap-4'>
                <div className={`flex ${isMobile ? 'flex-col pb-8' : 'flex-row'} p-0 gap-8`}>
                    {getAppBadges(true, isMobile, translationStore)}
                    {getAppBadges(false, isMobile, translationStore)}
                </div>
            </div>
        </div>
    );
};
